import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit, Trash2, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, API, inr } from './adminApi';
import { SEGMENTS } from '../../lib/catalog';
import ProductEditor from './ProductEditor';

const img = (p) => (p.images?.[0]?.startsWith('http') ? p.images[0] : `${API}${p.images?.[0] || ''}`);
const inStock = (p) => p.in_stock !== false && p.stock > 0;

const BULK = [['in_stock', 'Mark in stock'], ['out_of_stock', 'Mark out of stock'], ['show', 'Show on site'], ['hide', 'Hide from site'], ['delete', 'Delete']];

const Row = ({ p, checked, onCheck, onEdit, act }) => (
  <tr className={`border-b border-[var(--si-line)] ${p.published === false ? 'opacity-60' : ''}`} data-testid={`admin-row-${p.slug}`}>
    <td className="py-2"><input type="checkbox" checked={checked} onChange={onCheck} className="w-4 h-4" data-testid={`admin-check-${p.slug}`} /></td>
    <td><img src={img(p)} alt="" className="w-11 h-14 object-cover rounded" /></td>
    <td className="font-medium max-w-[260px]">{p.name}{p.coin_buttons && <span className="ml-1 text-[10px] font-bold text-[#92400E]">COIN</span>}</td>
    <td>{p.segment ? `₹${p.segment}` : '-'}{p.offer && <span className="ml-1 text-[10px] font-bold text-[#138A36]">{p.offer.toUpperCase()}</span>}</td>
    <td>{inr(p.price)} <span className="line-through text-xs text-[var(--si-grey)]">{p.original_price ? inr(p.original_price) : ''}</span></td>
    <td className={p.stock <= 5 ? 'font-bold text-[#B45309]' : ''}>{p.stock}</td>
    <td>
      <button onClick={() => act(() => api.setStock(p.slug, !inStock(p)))} data-testid={`admin-stock-toggle-${p.slug}`}
        className={`px-2.5 py-1 rounded text-xs font-bold border ${inStock(p) ? 'bg-[#E8F5E9] text-[#138A36] border-[#A7DDB5]' : 'bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]'}`}>
        {inStock(p) ? 'In stock' : 'Out of stock'}
      </button>
    </td>
    <td>
      <button onClick={() => act(() => api.setVisibility(p.slug, p.published === false))} data-testid={`admin-visibility-${p.slug}`}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold border ${p.published === false ? 'bg-gray-100 text-gray-700' : 'bg-[#EEF2FB] text-[var(--si-navy)]'}`}>
        {p.published === false ? <><EyeOff className="w-3 h-3" /> Hidden</> : <><Eye className="w-3 h-3" /> Visible</>}
      </button>
    </td>
    <td className="whitespace-nowrap">
      <button onClick={onEdit} title="Edit" className="p-1.5 hover:text-[var(--si-blue)]" data-testid={`admin-edit-${p.slug}`}><Edit className="w-4 h-4" /></button>
      <button onClick={() => act(() => api.duplicate(p.slug))} title="Duplicate" className="p-1.5 hover:text-[var(--si-blue)]" data-testid={`admin-duplicate-${p.slug}`}><Copy className="w-4 h-4" /></button>
      <button onClick={() => window.confirm(`Delete ${p.name}?`) && act(() => api.deleteProduct(p.slug))} title="Delete" className="p-1.5 hover:text-red-600" data-testid={`admin-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
    </td>
  </tr>
);

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [seg, setSeg] = useState('');
  const [view, setView] = useState('');
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(async () => { const d = await api.products(); setProducts(d.products || []); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); toast.success('Saved'); load(); } catch (e) { toast.error(e.message); } };

  const shown = products.filter((p) => (!seg || String(p.segment) === seg) && (!search || p.name.toLowerCase().includes(search.toLowerCase()))
    && (!view || (view === 'oos' && !inStock(p)) || (view === 'hidden' && p.published === false) || (view === 'low' && p.stock > 0 && p.stock <= 5)));
  const allSel = shown.length > 0 && shown.every((p) => sel.includes(p.slug));
  const bulk = async (action) => {
    if (action === 'delete' && !window.confirm(`Delete ${sel.length} products?`)) return;
    await act(() => api.bulk(sel, action)); setSel([]);
  };

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex flex-wrap items-center gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" className="si-input !w-56 !py-2" data-testid="admin-search" />
        <select value={seg} onChange={(e) => setSeg(e.target.value)} className="si-input !w-36 !py-2" data-testid="admin-segment-filter">
          <option value="">All segments</option>{SEGMENTS.map((x) => <option key={x.price} value={x.price}>₹{x.price}</option>)}
        </select>
        <select value={view} onChange={(e) => setView(e.target.value)} className="si-input !w-40 !py-2" data-testid="admin-view-filter">
          <option value="">All products</option><option value="oos">Out of stock</option><option value="low">Low stock (≤5)</option><option value="hidden">Hidden</option>
        </select>
        <p className="text-sm text-[var(--si-grey)]">{shown.length} of {products.length}</p>
        <div className="ml-auto flex gap-2">
          <button onClick={load} className="si-btn si-btn-outline !py-2 !px-3"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setEditing(null)} className="si-btn si-btn-primary !py-2 !px-4 text-sm" data-testid="admin-new-product"><Plus className="w-4 h-4" /> Add product</button>
        </div>
      </div>
      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#EEF2FB] p-3" data-testid="admin-bulk-bar">
          <b className="text-sm">{sel.length} selected:</b>
          {BULK.map(([k, l]) => <button key={k} onClick={() => bulk(k)} data-testid={`admin-bulk-${k}`}
            className={`px-3 py-1.5 rounded text-sm font-semibold border bg-white ${k === 'delete' ? 'text-red-600 border-red-300' : 'border-[var(--si-line)]'}`}>{l}</button>)}
          <button onClick={() => setSel([])} className="text-sm underline ml-auto">Clear</button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="admin-products-table">
          <thead><tr className="border-b border-[var(--si-line)] text-left text-xs uppercase tracking-wider text-[var(--si-grey)]">
            <th className="py-2"><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : shown.map((p) => p.slug))} className="w-4 h-4" data-testid="admin-check-all" /></th>
            <th>Image</th><th>Name</th><th>Segment</th><th>Price / MRP</th><th>Qty</th><th>Stock</th><th>Visibility</th><th>Actions</th></tr></thead>
          <tbody>
            {shown.map((p) => <Row key={p.slug} p={p} checked={sel.includes(p.slug)} act={act} onEdit={() => setEditing(p)}
              onCheck={() => setSel(sel.includes(p.slug) ? sel.filter((s) => s !== p.slug) : [...sel, p.slug])} />)}
          </tbody>
        </table>
      </div>
      {editing !== undefined && <ProductEditor existing={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); toast.success('Product saved'); load(); }} />}
    </div>
  );
};

export default AdminProducts;
